import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { TRADE_TYPE } from 'src/common/constants';
import { currencyFormatter } from 'src/common/helpers';
import {
    Button,
    Modal,
    ModalTitle,
    ModalContent,
    ModalActions,
    Tabs,
    Tab,
} from 'src/components/core';
import { Cross } from 'src/components/core/icons';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { setRiskLimit, showNotification } from 'src/setup/redux/slices/derivativesSlice';
import { RiskLimit } from 'src/types/configV2';

interface RiskLimitModalProps {
    open: boolean;
    currentRiskInfo: RiskLimit;
    side: string;
    handleClose: () => void;
}

const StyledModal = styled(Modal)`
    ${({ theme: { typography, breakpoints } }) => `
        .modal-paper {
            width: 343px;
            @media (min-width: ${breakpoints.md}) {
                width: 429px;
            }
        }
        .modal-title {
            padding: 16px 16px 24px;
        }
        .modal-content {
            padding: 0 16px;
            @media (max-width: ${breakpoints.md}) {
            padding: 8px;
        }
        }
        .modal-actions {
            padding: 24px 16px 15px;
            justify-content: center;
            gap: 16px;
            button {
                line-height: calc(${typography.lineHeight[3]} - 0.5px);
                font-weight: ${typography.weight.medium};
                padding-top: 10px;
                padding-bottom: 9px;
            }
        }
    `}
`;

const TitleText = styled.div`
    ${({ theme: { typography } }) => `
        display: inline-block;
        font-size: ${typography.size.desktop.paragraph.p1}; 
        line-height: ${typography.lineHeight[4]};
        font-weight: ${typography.weight.medium};
    `}
`;

const TabContainer = styled.div`
    ${({ theme: { colors, typography, breakpoints } }) => `
       border-bottom: 1px solid ${colors.border.light};
        margin-bottom: 24px;
        & > .tabs {
            padding: 0;
            & > .tab {
                min-width: 116.33px;
                justify-content: center;
                line-height: calc(${typography.lineHeight[3]} - 0.5px);
                @media (max-width: ${breakpoints.md}) {
                    min-width: 90px;
                    font-size: ${typography.size.mobile.paragraph.p1}; 
                    line-height: calc(${typography.lineHeight[3]} - 0.5px);
                }
            }
        }
    `}
`;

const StyledTable = styled.table`
    ${({ theme: { colors } }) => `
        border: 1px solid ${colors.border.light};
        border-radius: 8px;
        width: 100%;
        display: table;
    `}
`;

const StyledTRow = styled.tr`
    ${({ color }) => `
            color: ${color};
            text-align: left;
    `}
`;
const StyledThead = styled.th`
    ${({ theme: { colors, typography, breakpoints } }) => `
        font-size: ${typography.size.desktop.paragraph.p3}; 
        line-height: ${typography.lineHeight[2]};
        font-weight: ${typography.weight.normal};
        border-bottom: 1px solid ${colors.border.light};
        padding: 10px;
        text-align: center;
        @media (max-width: ${breakpoints.md}) {
            padding: 6px;
        }
        :nth-of-type(1) {
            width: 58px;
        }
        :nth-of-type(2) {
            width: 80px;
        }
        :nth-of-type(3) {
            width: 97px;
        }
        :nth-of-type(4) {
            width: 63px;
        }
    `}
`;

const StyledTd = styled.td`
    ${({ theme: { colors, typography, breakpoints } }) => `
        text-align: center;
        padding: 10px;
        @media (max-width: ${breakpoints.md}) {
            padding: 6px;
        }
        font-size: ${typography.size.desktop.paragraph.p3}; 
        line-height: ${typography.lineHeight[4]};
        font-weight: ${typography.weight.normal};
        line-height: ${typography.lineHeight[2]};
        &.border-bottom {
            border-bottom: 1px solid ${colors.border.light};
        }
    `}
`;

const possibleLeverages: number[] = [100, 50, 25, 10];

const RiskLimitModal: FC<RiskLimitModalProps> = ({ open, currentRiskInfo, side, handleClose }) => {
    const { t } = useTranslation();
    const { colors } = useTheme();
    const {
        currentSymbol,
        leverage: { leverageLong, leverageShort },
    } = useAppSelector((state) => state.derivatives);
    const setRiskLimitLoading = useAppSelector((state) => state.derivatives.loading.setRiskLimit);
    const { userCurrentFutureTradeAccountIndex } = useAppSelector((state) => state.global);
    const [activeTab, setActiveTab] = useState(currentRiskInfo.riskLimitId);
    const [updatedRiskInfo, setUpdatedRiskInfo] = useState(
        currentSymbol?.baseTokenFutures?.riskLimits.find(
            (item) => currentRiskInfo.riskLimitId == item.riskLimitId,
        ) || ({} as RiskLimit),
    );

    const dispatch = useAppDispatch();

    useEffect(() => {
        setUpdatedRiskInfo(
            currentSymbol?.baseTokenFutures?.riskLimits.find(
                (item) => activeTab == item.riskLimitId,
            ) || ({} as RiskLimit),
        );
    }, [activeTab, currentSymbol]);

    const handleConfirm = () => {
        const leverage = 1 / Number(updatedRiskInfo.initialMargin);
        const maxLeverage: number = Number(
            possibleLeverages[possibleLeverages.findIndex((val) => Number(val) <= leverage)],
        );
        if (
            (side === TRADE_TYPE.BUY_OPEN && maxLeverage < leverageLong) ||
            (side === TRADE_TYPE.SELL_OPEN && maxLeverage < leverageShort)
        ) {
            dispatch(
                showNotification({
                    title: 'error_invalid_leverage_title',
                    description: 'error_invalid_leverage_description',
                    type: 'error',
                }),
            );
        } else {
            dispatch(
                setRiskLimit({
                    side: side == TRADE_TYPE.BUY_OPEN ? TRADE_TYPE.BUY_OPEN : TRADE_TYPE.SELL_OPEN,
                    risk_limit_id: updatedRiskInfo.riskLimitId,
                    underlying_id: currentSymbol.secondLevelUnderlyingId,
                    symbol_id: currentSymbol.symbolId,
                    exchange_id: currentSymbol.exchangeId,
                    account_index: userCurrentFutureTradeAccountIndex,
                }),
            );
        }
        handleClose();
    };

    return (
        <StyledModal size="md" open={open}>
            <ModalTitle>
                <TitleText>
                    {t('set_risk_limit', { side: side == TRADE_TYPE.BUY_OPEN ? 'Long' : 'Short' })}
                </TitleText>
                <Cross
                    fill={colors.text.primary}
                    onClick={handleClose}
                    className="clickable"
                    style={{ height: 20, width: 20 }}
                />
            </ModalTitle>
            <ModalContent>
                <TabContainer>
                    <Tabs value={activeTab} onChange={setActiveTab}>
                        {currentSymbol?.baseTokenFutures?.riskLimits.map((item) => {
                            return (
                                <Tab
                                    value={item.riskLimitId}
                                    key={item.riskLimitId}
                                    label={currencyFormatter(Number(item.riskLimitAmount), 0)}
                                />
                            );
                        })}
                    </Tabs>
                </TabContainer>
                <StyledTable>
                    <StyledTRow color={colors.text.secondary}>
                        <StyledThead></StyledThead>
                        <StyledThead>{t('risk_limit')}</StyledThead>
                        <StyledThead>{t('maintenance_margin')}</StyledThead>
                        <StyledThead>{t('initial_margin')}</StyledThead>
                    </StyledTRow>
                    <StyledTRow color={colors.text.tertiary}>
                        <StyledTd className={'border-bottom'}>{t('current')}</StyledTd>
                        <StyledTd className={'border-bottom'}>
                            {`${currencyFormatter(Number(currentRiskInfo.riskLimitAmount), 0)} ${t(
                                'qty',
                            )}`}
                        </StyledTd>
                        <StyledTd className={'border-bottom'}>
                            {(Number(currentRiskInfo.maintainMargin) * 100).toFixed(1)}%
                        </StyledTd>
                        <StyledTd className={'border-bottom'}>
                            {(Number(currentRiskInfo.initialMargin) * 100).toFixed(1)}%
                        </StyledTd>
                    </StyledTRow>
                    <StyledTRow color={colors.text.secondary}>
                        <StyledTd>{t('updated')}</StyledTd>
                        <StyledTd>
                            {`${currencyFormatter(Number(updatedRiskInfo.riskLimitAmount), 0)} ${t(
                                'qty',
                            )}`}
                        </StyledTd>
                        <StyledTd>
                            {(Number(updatedRiskInfo.maintainMargin) * 100).toFixed(1)}%
                        </StyledTd>
                        <StyledTd>
                            {(Number(updatedRiskInfo.initialMargin) * 100).toFixed(1)}%
                        </StyledTd>
                    </StyledTRow>
                </StyledTable>
            </ModalContent>
            <ModalActions>
                <Button loading={setRiskLimitLoading} onClick={handleConfirm} fullWidth>
                    {t('submit')}
                </Button>
                <Button variant="outlined" onClick={handleClose} fullWidth>
                    {t('cancel')}
                </Button>
            </ModalActions>
        </StyledModal>
    );
};

export { RiskLimitModal };
